/**
 * The `org_units` a result layer paints, and which results have none on screen — the shared
 * half of §F6's result layers, read by both `ResultLayers` (the paint) and the §F5.6 legend
 * block (e) (the dimmed row).
 *
 * A hook rather than a store slice, for the same reason `choroClasses.ts` is one (NOTES
 * #190): the input is already shared, cached server state — the very `['units', …]` key the
 * §F5.3 choropleth holds — so a second caller costs one `useMemo`, no fetch and no render
 * pass, where a slice would mean one component writing state from an effect and another
 * reading it a frame later.
 *
 * **Why results are drawn from the explorer's own polygons at all.** They could be fetched
 * per result, keyed by the ids the run named. They are not, because then a result outline
 * would be drawn from geometry at a different generalisation from the boundary underneath
 * it, and a 3.4 px outline sitting a pixel off its own commune is exactly the kind of
 * detail that makes a reader distrust the number beside it. The price is that a result is
 * paintable only while its units are in the current geography — and the price is paid as a
 * **dimmed legend row, never a deletion**: a country change must not throw away an analysis
 * the reader waited for.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchUnits, queryKeys, unitsQueryFor, type UnitsFC } from '../../lib/api';
import { selectScopeParent, useAppStore, type ResultLayerSpec } from '../../lib/state';

/**
 * The `org_units` payload the explorer is currently drawn from.
 *
 * `enabled` is the caller's "is there anything to paint": with no result layers this must
 * not put a `/hierarchy/units` request on the wire that the explorer was not already making.
 */
export function useResultUnits(enabled: boolean): UnitsFC | undefined {
  const level = useAppStore((s) => s.reportingLevel);
  const country = useAppStore((s) => s.country);
  const parent = useAppStore(selectScopeParent);
  const query = unitsQueryFor(level);
  const { data } = useQuery({
    queryKey: queryKeys.units(query.type, query.level, country, 'z7', parent),
    queryFn: () => fetchUnits(query.type, query.level, country, 'z7', parent),
    enabled,
  });
  return data;
}

/** The unit ids a spec wants painted — `unit_ids` for `units`, `values`' keys for a choropleth. */
export function specUnitIds(spec: ResultLayerSpec): string[] {
  if (spec.kind === 'units') return spec.unit_ids ?? [];
  if (spec.kind === 'choropleth') return Object.keys(spec.values ?? {});
  return [];
}

/**
 * Which results have **no** unit in the geography now on screen — §F5.6 block (e)'s dimmed
 * rows.
 *
 * An artefact-backed result (`geojson`/`points`) is never in this set: its geometry is its
 * own and does not depend on the level or the country the reader is on.
 */
export function useOutOfViewResults(): Set<string> {
  const results = useAppStore((s) => s.results);
  const units = useResultUnits(results.length > 0);

  return useMemo(() => {
    const out = new Set<string>();
    // No payload yet is not "out of view" — it is "not answered yet", and a legend that
    // called a result out of view for the length of a fetch would print a falsehood about
    // the reader's own work (the rule block (b) applies to "no values reported").
    if (!units) return out;
    const present = new Set(units.features.map((f) => f.properties.id));
    for (const result of results) {
      const wanted = specUnitIds(result.spec);
      if (wanted.length && !wanted.some((id) => present.has(id))) out.add(result.id);
    }
    return out;
  }, [results, units]);
}
